// POT and CUT as the law computes them, which is not as a footprint over a plot.
//
// Under Legea 169/2026 the POT numerator is the projection on the ground of
// the whole building, balconies and cantilevers included, plus any building
// that stays on the plot. A plan approved under the old law (350/2001) keeps
// its own reading: the footprint at ground level. CUT counts enclosed floor
// area only, so balconies leave it while they enter POT.
import { DEROGATION, type Derogation } from "../rules/catuc.ts";
import { area, union, type MultiPoly } from "./geom.ts";
import type { Programme, Rules } from "./rules.ts";

export interface Limits {
  /** Ground projection allowed, m², after any derogation and minus what already stands. */
  maxProjection: number;
  /** Above-ground floor area allowed, m², likewise. */
  maxGross: number;
  potLimit: number; // %, after derogation
  cutLimit: number;
  factor: number;
}

export function limits(rules: Rules, plotArea: number): Limits {
  const factor = DEROGATION[rules.derogation ?? "none"].factor;
  const potLimit = rules.pot * (1 + factor);
  const cutLimit = rules.cut * (1 + factor);
  return {
    potLimit, cutLimit, factor,
    maxProjection: Math.max(0, (potLimit / 100) * plotArea - (rules.existing?.footprint ?? 0)),
    maxGross: Math.max(0, cutLimit * plotArea - (rules.existing?.gross ?? 0)),
  };
}

/**
 * What balconies add to the ground projection, as a share of the footprint,
 * before the flats are drawn: one balcony per flat, `share` of its frontage
 * wide and `depth` deep, on one facade; a corridor wing has flats on both
 * facades. Used to keep a candidate under POT; the drawn balconies are what
 * the report checks.
 */
export function balconyAllowance(rules: Rules, prog: Programme, wingDepth: number, corridor: boolean): number {
  if ((rules.regime ?? "catuc") !== "catuc" || prog.balconyDepth <= 0) return 0;
  return ((corridor ? 2 : 1) * prog.balconyDepth * prog.balconyShare) / wingDepth;
}

export interface Indicators {
  /** POT numerator, m²: the footprint, or the projection under CATUC, plus what stays. */
  projection: number;
  pot: number; // %
  /** CUT numerator, m²: enclosed above-ground area plus what stays. Balconies excluded. */
  gross: number;
  cut: number;
  balconies: number; // m² of balcony projection beyond the footprint
}

export function indicators(rules: Rules, plotArea: number, storeys: MultiPoly[], balconies: MultiPoly[], grossArea: number): Indicators {
  const catuc = (rules.regime ?? "catuc") === "catuc";
  const footprint = storeys[0] ?? [];
  const projected = catuc ? area(union(...storeys, ...balconies)) : area(footprint);
  const projection = projected + (rules.existing?.footprint ?? 0);
  const gross = grossArea + (rules.existing?.gross ?? 0);
  return {
    projection,
    pot: (projection / plotArea) * 100,
    gross,
    cut: gross / plotArea,
    balconies: catuc ? projected - area(union(...storeys)) : 0,
  };
}

export type { Derogation };
